/**
 * CSS injecté dans le Shadow Root au montage. Le shadow DOM isole déjà les
 * styles dans les deux sens ; le préfixe `bw-` et le scope [data-bug-widget]
 * sont conservés par lisibilité et pour les éléments hérités (font, color).
 */
const CSS = `
[data-bug-widget] {
  --bw-accent: #8A4FFF;
  --bw-accent-hover: #7a3ff0;
  --bw-ink: #1c1a27;
  --bw-muted: #6b6880;
  --bw-border: #e6e3f0;
  --bw-bg: #ffffff;
  --bw-bg-soft: #f7f5fc;
  --bw-danger: #d64545;
  --bw-radius: 12px;
  --bw-shadow: 0 4px 24px rgba(28, 26, 39, 0.16);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
  line-height: 1.45;
  color: var(--bw-ink);
  box-sizing: border-box;
  text-align: left;
}
[data-bug-widget] *, [data-bug-widget] *::before, [data-bug-widget] *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  text-transform: none;
  text-decoration: none;
  letter-spacing: normal;
  text-shadow: none;
}
[data-bug-widget] button {
  background: none;
  border: none;
  cursor: pointer;
  box-shadow: none;
  border-radius: 0;
}

/* ---- Pastille ---- */
[data-bug-widget] .bw-fab-zone {
  position: fixed;
  right: 20px;
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  z-index: 2147483646;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}
[data-bug-widget] .bw-fab {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--bw-accent);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 12px rgba(138, 79, 255, 0.4);
  transition: transform 0.15s ease, background 0.15s ease;
}
[data-bug-widget] .bw-fab:hover { background: var(--bw-accent-hover); transform: scale(1.06); }
[data-bug-widget] .bw-fab svg { width: 22px; height: 22px; display: block; }

[data-bug-widget] .bw-menu {
  background: var(--bw-bg);
  border: 1px solid var(--bw-border);
  border-radius: var(--bw-radius);
  box-shadow: var(--bw-shadow);
  overflow: hidden;
  min-width: 220px;
}
[data-bug-widget] .bw-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  padding: 11px 14px;
  font-size: 13.5px;
  color: var(--bw-ink);
  transition: background 0.12s ease;
}
[data-bug-widget] .bw-menu-item:hover { background: var(--bw-bg-soft); }
[data-bug-widget] .bw-menu-item svg { width: 16px; height: 16px; color: var(--bw-accent); flex-shrink: 0; }
[data-bug-widget] .bw-menu-sep { height: 1px; background: var(--bw-border); }
[data-bug-widget] .bw-menu-item.bw-menu-item-muted { color: var(--bw-muted); font-size: 12.5px; }
[data-bug-widget] .bw-menu-item.bw-menu-item-muted svg { color: var(--bw-muted); }

/* ---- Mode inspection ---- */
[data-bug-widget] .bw-inspect-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  cursor: crosshair;
}
[data-bug-widget] .bw-inspect-highlight {
  position: fixed;
  pointer-events: none;
  border: 2px solid var(--bw-accent);
  border-radius: 3px;
  background: rgba(138, 79, 255, 0.08);
  z-index: 2147483647;
  transition: all 0.06s ease-out;
}
[data-bug-widget] .bw-inspect-label {
  position: absolute;
  top: -26px;
  left: -2px;
  background: var(--bw-accent);
  color: #fff;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  white-space: nowrap;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
}
[data-bug-widget] .bw-inspect-label.bw-inspect-label-below { top: auto; bottom: -26px; }
[data-bug-widget] .bw-inspect-toolbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2147483647;
  background: var(--bw-bg);
  border: 1px solid var(--bw-border);
  border-radius: 999px;
  box-shadow: var(--bw-shadow);
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 13px;
  color: var(--bw-muted);
}
[data-bug-widget] .bw-inspect-toolbar .bw-link-btn { color: var(--bw-accent); font-weight: 600; font-size: 13px; }
[data-bug-widget] .bw-inspect-toolbar .bw-link-btn:hover { text-decoration: underline; }

/* ---- Capture de zone ---- */
[data-bug-widget] .bw-capture-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  cursor: crosshair;
  background: rgba(20, 18, 30, 0.22);
  touch-action: none;
}
[data-bug-widget] .bw-capture-overlay.bw-capture-overlay-active { background: transparent; }
[data-bug-widget] .bw-capture-selection {
  position: fixed;
  border: 2px solid var(--bw-accent);
  background: transparent;
  box-shadow: 0 0 0 200vmax rgba(20, 18, 30, 0.35);
  pointer-events: none;
}
[data-bug-widget] .bw-capture-size {
  position: absolute;
  right: 0;
  bottom: -26px;
  background: var(--bw-accent);
  color: #fff;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  white-space: nowrap;
}
[data-bug-widget] .bw-link-btn.bw-link-btn-muted { color: var(--bw-muted); font-weight: 500; }

/* ---- Panneau ---- */
[data-bug-widget] .bw-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2147483647;
  width: min(460px, 100vw);
  height: 100dvh;
  background: var(--bw-bg);
  border-left: 1px solid var(--bw-border);
  box-shadow: -8px 0 32px rgba(28, 26, 39, 0.14);
  display: flex;
  flex-direction: column;
  animation: bw-slide-in 0.22s ease-out;
}
@keyframes bw-slide-in {
  from { transform: translateX(24px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}
[data-bug-widget] .bw-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--bw-border);
}
[data-bug-widget] .bw-panel-title { font-size: 15px; font-weight: 700; }
[data-bug-widget] .bw-close {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bw-muted);
}
[data-bug-widget] .bw-close:hover { background: var(--bw-bg-soft); }
[data-bug-widget] .bw-panel-body { flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; padding: 18px 20px; display: flex; flex-direction: column; gap: 14px; }
[data-bug-widget] .bw-panel-footer { padding: 14px 20px calc(14px + env(safe-area-inset-bottom, 0px)); border-top: 1px solid var(--bw-border); }

[data-bug-widget] .bw-field { display: flex; flex-direction: column; gap: 5px; }
[data-bug-widget] .bw-label { font-size: 12.5px; font-weight: 600; color: var(--bw-ink); }
[data-bug-widget] .bw-label .bw-optional { font-weight: 400; color: var(--bw-muted); }
[data-bug-widget] .bw-input, [data-bug-widget] .bw-textarea {
  width: 100%;
  border: 1px solid var(--bw-border);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13.5px;
  background: var(--bw-bg);
  color: var(--bw-ink);
  outline: none;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}
[data-bug-widget] .bw-input:focus, [data-bug-widget] .bw-textarea:focus {
  border-color: var(--bw-accent);
  box-shadow: 0 0 0 3px rgba(138, 79, 255, 0.15);
}
[data-bug-widget] .bw-textarea { min-height: 96px; resize: vertical; }
[data-bug-widget] .bw-row { display: flex; gap: 10px; }
[data-bug-widget] .bw-row .bw-field { flex: 1; }

[data-bug-widget] .bw-target-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bw-bg-soft);
  border: 1px solid var(--bw-border);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12.5px;
  color: var(--bw-muted);
}
[data-bug-widget] .bw-target-chip code { flex: 1; min-width: 0; }
[data-bug-widget] .bw-target-add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  border: 1px dashed var(--bw-border);
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--bw-accent);
  background: var(--bw-bg);
  transition: border-color 0.12s ease, background 0.12s ease;
}
[data-bug-widget] .bw-target-add:hover { border-color: var(--bw-accent); background: var(--bw-bg-soft); }
[data-bug-widget] .bw-target-add svg { width: 16px; height: 16px; flex-shrink: 0; }
[data-bug-widget] .bw-target-add .bw-optional { font-weight: 400; color: var(--bw-muted); }
[data-bug-widget] .bw-target-chip code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11.5px;
  color: var(--bw-accent);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

[data-bug-widget] .bw-shot { position: relative; border: 1px solid var(--bw-border); border-radius: 8px; overflow: hidden; }
[data-bug-widget] .bw-shot img { display: block; width: 100%; max-height: 180px; object-fit: cover; object-position: top; }
[data-bug-widget] .bw-shot-off { opacity: 0.35; filter: grayscale(1); }
[data-bug-widget] .bw-shot-note { font-size: 12px; color: var(--bw-muted); font-style: italic; }

[data-bug-widget] .bw-attach {
  background: var(--bw-bg-soft);
  border: 1px solid var(--bw-border);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12.5px;
  color: var(--bw-muted);
}
[data-bug-widget] .bw-attach-summary { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
[data-bug-widget] .bw-attach-detail { margin-top: 8px; border-top: 1px solid var(--bw-border); padding-top: 8px; max-height: 180px; overflow-y: auto; }
[data-bug-widget] .bw-attach-detail ul { list-style: none; }
[data-bug-widget] .bw-attach-detail li {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10.5px;
  padding: 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
[data-bug-widget] .bw-attach-detail li.bw-log-error { color: var(--bw-danger); }
[data-bug-widget] .bw-attach-detail li.bw-log-warn { color: #a06a00; }
[data-bug-widget] .bw-check { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--bw-ink); cursor: pointer; }
[data-bug-widget] .bw-check input { accent-color: var(--bw-accent); width: 14px; height: 14px; cursor: pointer; }

[data-bug-widget] .bw-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 10px 16px;
  border-radius: 999px;
  background: var(--bw-accent);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  transition: background 0.15s ease;
}
[data-bug-widget] .bw-btn:hover:not(:disabled) { background: var(--bw-accent-hover); }
[data-bug-widget] .bw-btn:disabled { opacity: 0.55; cursor: default; }
[data-bug-widget] .bw-btn-ghost {
  background: transparent;
  color: var(--bw-muted);
  border: 1px solid var(--bw-border);
  font-weight: 500;
}
[data-bug-widget] .bw-btn-ghost:hover:not(:disabled) { background: var(--bw-bg-soft); color: var(--bw-ink); }
[data-bug-widget] .bw-link-btn { color: var(--bw-accent); font-size: 12.5px; font-weight: 600; }
[data-bug-widget] .bw-link-btn:hover { text-decoration: underline; }

[data-bug-widget] .bw-error {
  background: #fdf0f0;
  border: 1px solid #f2c9c9;
  color: var(--bw-danger);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12.5px;
}

[data-bug-widget] .bw-spinner {
  width: 15px;
  height: 15px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: bw-spin 0.7s linear infinite;
}
@keyframes bw-spin { to { transform: rotate(360deg); } }

/* ---- Confirmation ---- */
[data-bug-widget] .bw-success { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 32px 8px; text-align: center; }
[data-bug-widget] .bw-success-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(138, 79, 255, 0.12);
  color: var(--bw-accent);
  display: flex;
  align-items: center;
  justify-content: center;
}
[data-bug-widget] .bw-success-icon svg { width: 24px; height: 24px; }
[data-bug-widget] .bw-success-title { font-size: 16px; font-weight: 700; }
[data-bug-widget] .bw-success a {
  color: var(--bw-accent);
  font-weight: 600;
  font-size: 13.5px;
  text-decoration: none;
}
[data-bug-widget] .bw-success a:hover { text-decoration: underline; }

/* ---- Badge de la pastille ---- */
[data-bug-widget] .bw-fab-wrap { position: relative; }
[data-bug-widget] .bw-fab-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #e67e22;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #fff;
  pointer-events: none;
}
[data-bug-widget] .bw-menu-badges {
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
}
[data-bug-widget] .bw-menu-badge {
  background: #fdeede;
  color: #a15511;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  padding: 2px 8px;
  white-space: nowrap;
}
[data-bug-widget] .bw-menu-badge-info { background: #e3f3fd; color: #0b6aa3; }

/* ---- Onglets du panneau ---- */
[data-bug-widget] .bw-tabs {
  display: flex;
  border-bottom: 1px solid var(--bw-border);
  padding: 0 12px;
  gap: 4px;
  flex-shrink: 0;
}
[data-bug-widget] .bw-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 10px 12px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--bw-muted);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.12s ease, border-color 0.12s ease;
}
[data-bug-widget] .bw-tab:hover { color: var(--bw-ink); }
[data-bug-widget] .bw-tab.bw-tab-active { color: var(--bw-accent); border-bottom-color: var(--bw-accent); }
[data-bug-widget] .bw-tab-badge {
  background: #e67e22;
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  border-radius: 999px;
  min-width: 16px;
  height: 16px;
  padding: 0 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ---- Couleurs de statut (sémantique du tracker, tons doux) ---- */
[data-bug-widget] .bw-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  padding: 2px 8px;
  background: var(--bw-bg-soft);
  color: var(--bw-muted);
  white-space: nowrap;
}
[data-bug-widget] .bw-pill::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
[data-bug-widget] .bw-st-DETECTED { background: #fdeaea; color: #a13030; }
[data-bug-widget] .bw-st-IN_PROGRESS { background: #fdf3d9; color: #8a5d00; }
[data-bug-widget] .bw-st-RESOLVED_NOT_DEPLOYED { background: #ebeafd; color: #4640b5; }
[data-bug-widget] .bw-st-RESOLVED_DEPLOYED { background: #e3f3fd; color: #0b6aa3; }
[data-bug-widget] .bw-st-PENDING_CLIENT_VALIDATION { background: #fdeede; color: #a15511; }
[data-bug-widget] .bw-st-REJECTED { background: #fce7ef; color: #a11d4a; }
[data-bug-widget] .bw-st-VALIDATED { background: #e6f7ec; color: #1c7a41; }

/* ---- Suivi : liste groupée par statut ---- */
[data-bug-widget] .bw-track-banner {
  background: #fdeede;
  border: 1px solid #f5d5b5;
  color: #a15511;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 12.5px;
  font-weight: 600;
}
[data-bug-widget] .bw-track-banner.bw-track-banner-ok {
  background: #e6f7ec;
  border-color: #bce5cb;
  color: #1c7a41;
}
[data-bug-widget] .bw-track-section { display: flex; flex-direction: column; gap: 8px; }
[data-bug-widget] .bw-track-hint { margin: -2px 0 0; font-size: 12px; color: var(--bw-muted); }
[data-bug-widget] .bw-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: var(--bw-bg-soft);
}
[data-bug-widget] .bw-filter {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  color: var(--bw-muted);
  transition: background 0.12s ease, color 0.12s ease;
}
[data-bug-widget] .bw-filter:hover { color: var(--bw-ink); }
[data-bug-widget] .bw-filter.bw-filter-active {
  background: var(--bw-bg);
  color: var(--bw-ink);
  box-shadow: 0 1px 3px rgba(28, 26, 39, 0.1);
}
[data-bug-widget] .bw-filter-count {
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  padding: 0 6px;
  min-width: 18px;
  line-height: 17px;
  background: var(--bw-border);
  color: var(--bw-muted);
}
[data-bug-widget] .bw-filter-alert .bw-filter-count { background: #e67e22; color: #fff; }
[data-bug-widget] .bw-card-new { font-size: 11.5px; font-weight: 600; color: #0b6aa3; }
[data-bug-widget] .bw-track-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--bw-muted);
  margin-top: 4px;
}
[data-bug-widget] .bw-track-head .bw-track-count { font-weight: 600; color: var(--bw-muted); opacity: 0.8; }
[data-bug-widget] .bw-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  width: 100%;
  border: 1px solid var(--bw-border);
  border-radius: 10px;
  padding: 12px 14px;
  background: var(--bw-bg);
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  cursor: pointer;
}
[data-bug-widget] .bw-card:hover { border-color: var(--bw-accent); box-shadow: 0 2px 10px rgba(28, 26, 39, 0.07); }
[data-bug-widget] .bw-card-top { display: flex; align-items: flex-start; gap: 10px; }
[data-bug-widget] .bw-card-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
[data-bug-widget] .bw-card-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--bw-ink);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
[data-bug-widget] .bw-card-meta { display: flex; align-items: center; gap: 8px; font-size: 11.5px; color: var(--bw-muted); flex-wrap: wrap; }
[data-bug-widget] .bw-thumb {
  width: 52px;
  height: 52px;
  border-radius: 6px;
  border: 1px solid var(--bw-border);
  object-fit: cover;
  object-position: top;
  flex-shrink: 0;
  background: var(--bw-bg-soft);
}
[data-bug-widget] .bw-btn-sm {
  width: auto;
  align-self: flex-start;
  padding: 7px 14px;
  font-size: 12.5px;
}
[data-bug-widget] .bw-validate-ok {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #1c7a41;
  font-size: 12.5px;
  font-weight: 600;
}
[data-bug-widget] .bw-reject-ok { color: #a11d4a; font-size: 12.5px; font-weight: 600; }
[data-bug-widget] .bw-row-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
[data-bug-widget] .bw-btn-danger {
  background: var(--bw-danger);
}
[data-bug-widget] .bw-btn-danger:hover:not(:disabled) { background: #b93a3a; }
[data-bug-widget] .bw-btn-danger-ghost {
  background: transparent;
  color: var(--bw-danger);
  border: 1px solid #f2c9c9;
  font-weight: 600;
}
[data-bug-widget] .bw-btn-danger-ghost:hover:not(:disabled) { background: #fdf0f0; }
[data-bug-widget] .bw-reject-zone {
  border: 1px solid #f2c9c9;
  background: #fdf6f6;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
[data-bug-widget] .bw-reject-title { font-size: 12.5px; font-weight: 700; color: var(--bw-danger); }

/* ---- Suivi : commentaires ---- */
[data-bug-widget] .bw-comment-count {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--bw-muted);
}
[data-bug-widget] .bw-comment-count svg { flex-shrink: 0; }
[data-bug-widget] .bw-comment-count-new { color: #0b6aa3; font-weight: 700; }
[data-bug-widget] .bw-new-label {
  background: #e3f3fd;
  color: #0b6aa3;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-radius: 999px;
  padding: 1px 6px;
}
[data-bug-widget] .bw-track-banner.bw-track-banner-info {
  background: #e3f3fd;
  border-color: #bcdcf2;
  color: #0b6aa3;
}
[data-bug-widget] .bw-comments { display: flex; flex-direction: column; gap: 8px; }
[data-bug-widget] .bw-comment {
  border-radius: 10px;
  padding: 8px 11px;
  font-size: 12.5px;
  max-width: 94%;
}
[data-bug-widget] .bw-comment-team {
  background: var(--bw-bg-soft);
  border: 1px solid var(--bw-border);
  align-self: flex-start;
}
[data-bug-widget] .bw-comment-client {
  background: rgba(138, 79, 255, 0.09);
  border: 1px solid rgba(138, 79, 255, 0.18);
  align-self: flex-end;
}
[data-bug-widget] .bw-comment-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--bw-muted);
  margin-bottom: 3px;
}
[data-bug-widget] .bw-comment-meta strong { font-weight: 700; color: var(--bw-ink); }
[data-bug-widget] .bw-comment-tag {
  background: var(--bw-accent);
  color: #fff;
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  padding: 1px 6px;
}
[data-bug-widget] .bw-comment-body { white-space: pre-wrap; word-break: break-word; }
[data-bug-widget] .bw-comments-empty { font-size: 12.5px; color: var(--bw-muted); }
[data-bug-widget] .bw-comment-form { display: flex; flex-direction: column; gap: 8px; }
[data-bug-widget] .bw-textarea-sm { min-height: 64px; }

/* ---- Suivi : détail ---- */
[data-bug-widget] .bw-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--bw-muted);
  font-size: 12.5px;
  font-weight: 600;
  align-self: flex-start;
}
[data-bug-widget] .bw-back:hover { color: var(--bw-ink); }
[data-bug-widget] .bw-detail-title { font-size: 15px; font-weight: 700; line-height: 1.35; }
[data-bug-widget] .bw-detail-desc { font-size: 13px; color: var(--bw-ink); white-space: pre-wrap; }
[data-bug-widget] .bw-detail-shot { border: 1px solid var(--bw-border); border-radius: 8px; overflow: hidden; cursor: zoom-in; padding: 0; display: block; width: 100%; }
[data-bug-widget] .bw-detail-shot img { display: block; width: 100%; }
[data-bug-widget] .bw-detail-meta { font-size: 12px; color: var(--bw-muted); display: flex; flex-direction: column; gap: 3px; }
[data-bug-widget] .bw-detail-meta strong { font-weight: 600; color: var(--bw-ink); }
[data-bug-widget] .bw-detail-meta .bw-page-url { word-break: break-all; }

[data-bug-widget] .bw-timeline { display: flex; flex-direction: column; gap: 0; }
[data-bug-widget] .bw-timeline-item { display: flex; gap: 10px; }
[data-bug-widget] .bw-timeline-rail { display: flex; flex-direction: column; align-items: center; width: 10px; flex-shrink: 0; }
[data-bug-widget] .bw-timeline-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--bw-accent); margin-top: 5px; flex-shrink: 0; }
[data-bug-widget] .bw-timeline-line { flex: 1; width: 2px; background: var(--bw-border); margin-top: 3px; }
[data-bug-widget] .bw-timeline-body { padding-bottom: 14px; font-size: 12.5px; min-width: 0; }
[data-bug-widget] .bw-timeline-body .bw-timeline-when { font-size: 11.5px; color: var(--bw-muted); margin-top: 1px; }

/* ---- Zoom capture ---- */
[data-bug-widget] .bw-zoom {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  background: rgba(20, 18, 30, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  cursor: zoom-out;
  animation: bw-fade-in 0.15s ease-out;
}
@keyframes bw-fade-in { from { opacity: 0; } to { opacity: 1; } }
[data-bug-widget] .bw-zoom img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 6px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.5);
}

/* ---- États chargement / vide ---- */
[data-bug-widget] .bw-skeleton {
  border: 1px solid var(--bw-border);
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
[data-bug-widget] .bw-skeleton-line {
  height: 11px;
  border-radius: 5px;
  background: linear-gradient(90deg, var(--bw-bg-soft) 25%, #efedf7 45%, var(--bw-bg-soft) 65%);
  background-size: 200% 100%;
  animation: bw-shimmer 1.3s ease-in-out infinite;
}
@keyframes bw-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
[data-bug-widget] .bw-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 16px;
  text-align: center;
  color: var(--bw-muted);
  font-size: 13px;
}
[data-bug-widget] .bw-empty svg { width: 28px; height: 28px; opacity: 0.5; }
[data-bug-widget] .bw-retry-zone { display: flex; flex-direction: column; gap: 10px; align-items: flex-start; }

/* ---- Mobile : panneau plein écran, cibles tactiles élargies ---- */
@media (max-width: 640px) {
  [data-bug-widget] { font-size: 15px; }
  [data-bug-widget] .bw-panel {
    width: 100vw;
    border-left: none;
    animation: bw-slide-up 0.22s ease-out;
  }
  [data-bug-widget] .bw-panel-header { padding: 14px 16px; }
  [data-bug-widget] .bw-panel-body { padding: 16px; }
  [data-bug-widget] .bw-panel-footer { padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px)); }
  [data-bug-widget] .bw-close { width: 40px; height: 40px; }
  [data-bug-widget] .bw-fab { width: 56px; height: 56px; }
  [data-bug-widget] .bw-fab svg { width: 26px; height: 26px; }
  [data-bug-widget] .bw-fab-badge { min-width: 20px; height: 20px; font-size: 12px; }
  [data-bug-widget] .bw-btn { min-height: 48px; font-size: 15px; }
  /* 16px minimum : évite le zoom automatique d'iOS au focus des champs. */
  [data-bug-widget] .bw-input, [data-bug-widget] .bw-textarea { font-size: 16px; padding: 10px 12px; }
  [data-bug-widget] .bw-card { padding: 14px; }
  [data-bug-widget] .bw-card-title { font-size: 15px; }
  [data-bug-widget] .bw-card-meta { font-size: 12.5px; }
  [data-bug-widget] .bw-btn-sm { align-self: stretch; width: 100%; }
  [data-bug-widget] .bw-thumb { width: 56px; height: 56px; }
  [data-bug-widget] .bw-detail-title { font-size: 17px; }
  [data-bug-widget] .bw-detail-desc { font-size: 14.5px; }
  [data-bug-widget] .bw-back { min-height: 40px; }
  [data-bug-widget] .bw-inspect-toolbar {
    left: 12px;
    right: 12px;
    transform: none;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    flex-wrap: wrap;
    justify-content: center;
    border-radius: 16px;
  }
  [data-bug-widget] .bw-zoom { padding: 8px; }
}
@keyframes bw-slide-up {
  from { transform: translateY(24px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
`;

export function injectStyles(root: ShadowRoot): void {
  const style = document.createElement("style");
  style.textContent = CSS;
  root.appendChild(style);
}
